import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import MDBox from "components/MDBox";
import Box from "@mui/material/Box";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Paper from "@mui/material/Paper";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

function StudentEnrollmentActionsMenu({ row, onViewDetails, onDelete, deleteLabel = "Delete" }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Button
        variant="contained"
        size="medium"
        startIcon={<MoreVertIcon />}
        sx={{
          backgroundColor: "#1976d2",
          color: "#fff",
          textTransform: "none",
          "&:hover": { backgroundColor: "#115293" },
        }}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        Actions
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            handleClose();
            onViewDetails(row);
          }}
          sx={{ color: "blue", "&:hover": { backgroundColor: "rgba(0, 0, 255, 0.1)" } }}
        >
          <VisibilityIcon fontSize="small" style={{ marginRight: 8 }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            onDelete(row);
          }}
          sx={{ color: "red", "&:hover": { backgroundColor: "rgba(255, 0, 0, 0.1)" } }}
        >
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          {deleteLabel}
        </MenuItem>
      </Menu>
    </>
  );
}

StudentEnrollmentActionsMenu.propTypes = {
  row: PropTypes.object.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  deleteLabel: PropTypes.string,
};

function StudentEnrollement() {
  // ...existing code...
  const getEnrollFormErrors = () => {
    const errors = {};
    if (!enrollForm.student_name) errors.student_name = "Student name is required";
    if (!enrollForm.aadhaar_no) errors.aadhaar_no = "Aadhaar number is required";
    else if (!validateAadhaar(enrollForm.aadhaar_no)) errors.aadhaar_no = "Invalid Aadhaar number";
    if (!enrollForm.mobile) errors.mobile = "Mobile number is required";
    else if (!validateMobile(enrollForm.mobile)) errors.mobile = "Invalid mobile number";
    if (enrollForm.email && !validateEmail(enrollForm.email))
      errors.email = "Invalid email address";
    if (!enrollForm.dob) errors.dob = "Date of birth is required";
    if (!enrollForm.gender) errors.gender = "Gender is required";
    if (!enrollForm.admission_no) errors.admission_no = "Admission No is required";
    if (!enrollForm.admittedbatch_id) errors.admittedbatch_id = "Batch is required";
    if (!enrollForm.branch_id) errors.branch_id = "Branch is required";
    if (!enrollForm.seat_type) errors.seat_type = "Seat type is required";
    return errors;
  };

  const handleEnrollSave = async () => {
    console.debug("handleEnrollSave called", enrollForm);
    const errors = getEnrollFormErrors();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      showSnackbar("Please complete required fields before saving", "error");
      return;
    }
    // proceed to submit (reuse submit flow)
    try {
      await handleEnrollSubmit({ preventDefault: () => {} });
    } catch (err) {
      // handleEnrollSubmit will show alerts/snackbars on its own
    }
  };
  // All state and handlers
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const sessionUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const clientId = sessionUser?.client_id;
  const collegeId = sessionUser?.college_id;

  // Validation helpers
  const validateAadhaar = (aadhaar) => /^[2-9]{1}[0-9]{11}$/.test(aadhaar);
  const validateMobile = (mobile) => /^[6-9][0-9]{9}$/.test(mobile);
  const validateEmail = (email) => !email || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const formatDateForInput = (value) => {
    if (!value) return "";
    return String(value).slice(0, 10);
  };

  // Error state
  const [formErrors, setFormErrors] = useState({});

  // Handles form submit: uploads photo, then submits enrollment
  const handleEnrollSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    console.debug("handleEnrollSubmit called", enrollForm);
    if (!clientId || !collegeId) {
      showSnackbar("Missing client/college context", "error");
      return;
    }
    const errors = getEnrollFormErrors();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    let photo_url = "";
    try {
      if (enrollForm.photo) {
        const formData = new FormData();
        formData.append("photo", enrollForm.photo);
        const params = new URLSearchParams({
          college_id: enrollForm.college_id || collegeId,
          admittedbatch_id: enrollForm.admittedbatch_id || "",
          branch_id: enrollForm.branch_id || "",
          student_rollno: enrollForm.rollno || "",
        });
        const uploadRes = await fetch(
          `${API_BASE_URL || ""}/api/students/upload-photo?${params.toString()}`,
          {
            method: "POST",
            body: formData,
          }
        );
        const uploadText = await uploadRes.text();
        let uploadData;
        try {
          uploadData = JSON.parse(uploadText);
        } catch {
          showSnackbar("Photo upload failed: " + uploadText, "error");
          return;
        }
        if (uploadData.success && uploadData.photo_url) {
          photo_url = uploadData.photo_url;
        } else {
          showSnackbar("Photo upload failed: " + (uploadData.message || "Unknown error"), "error");
          return;
        }
      }
      // Normalize payload: ensure numeric IDs are numbers and rollno maps to student_rollno
      const normalized = { ...enrollForm };
      // Prefer explicit student_rollno, fall back to rollno
      normalized.student_rollno = normalized.student_rollno || normalized.rollno || "";
      normalized.student_name = normalized.student_name || normalized.fullname || "";
      normalized.father_name = normalized.father_name || normalized.fathername || "";
      normalized.mother_name = normalized.mother_name || normalized.mothername || "";
      normalized.student_status = normalized.student_status || normalized.studentstatus || "ACTIVE";
      if (
        normalized.admittedbatch_id !== undefined &&
        normalized.admittedbatch_id !== null &&
        normalized.admittedbatch_id !== ""
      ) {
        normalized.admittedbatch_id = Number(normalized.admittedbatch_id);
      }
      if (
        normalized.branch_id !== undefined &&
        normalized.branch_id !== null &&
        normalized.branch_id !== ""
      ) {
        normalized.branch_id = Number(normalized.branch_id);
      }
      if (
        normalized.client_id !== undefined &&
        normalized.client_id !== null &&
        normalized.client_id !== ""
      ) {
        normalized.client_id = Number(normalized.client_id);
      }
      if (
        normalized.college_id !== undefined &&
        normalized.college_id !== null &&
        normalized.college_id !== ""
      ) {
        normalized.college_id = Number(normalized.college_id);
      }
      normalized.photo_url = photo_url || enrollForm.photo_url || "";
      delete normalized.photo;
      // Log final payload for debugging (backend also logs createPayload)
      console.debug("[Enrollment] finalPayload:", normalized);
      const res = await fetch(
        `${API_BASE_URL || ""}/api/student-enrollment/${clientId}/${collegeId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(normalized),
        }
      );
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        showSnackbar("Server error: " + text, "error");
        return;
      }
      if (data.success) {
        showSnackbar("Student enrolled successfully!", "success");
        handleCloseEnrollDialog();
      } else {
        showSnackbar("Enrollment failed: " + (data.message || "Unknown error"), "error");
      }
    } catch (err) {
      showSnackbar("Error: " + err.message, "error");
    }
  };
  const [enrollTab, setEnrollTab] = useState(0);
  const [enrollForm, setEnrollForm] = useState({
    student_name: "",
    student_rollno: "",
    gender: "",
    dob: "",
    mobile: "",
    email: "",
    id_mark: "",
    identification_mark_1: "",
    identification_mark_2: "",
    rollno: "",
    surname: "",
    fullname: "",
    fathername: "",
    mothername: "",
    fatherphoneno: "",
    motherphoneno: "",
    bloodgroup: "",
    phctype: "",
    studentstatus: "",
    certificatesin: "",
    certificates_received: "",
    admission_no: "",
    admission_date: "",
    admittedbatch_id: "",
    branch_id: "",
    second_language: "",
    seat_type: "",
    section: "",
    academic_year: "",
    address: "",
  });
  const mandatoryFields = ["student_name", "student_rollno", "gender", "dob", "mobile", "email"];
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [relatedStudents, setRelatedStudents] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [studentDetailsDialogOpen, setStudentDetailsDialogOpen] = useState(false);
  const [studentDetails, setStudentDetails] = useState(null);
  const [studentSaveLoading, setStudentSaveLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [branches, setBranches] = useState([]);
  const [batchBranchMappings, setBatchBranchMappings] = useState([]);
  const [currentAcademicYear, setCurrentAcademicYear] = useState("");

  const handleOpenEnrollDialog = () => {
    setEnrollDialogOpen(true);
    setEnrollTab(0);
    setEnrollForm({
      student_name: "",
      student_rollno: "",
      gender: "",
      dob: "",
      mobile: "",
      email: "",
      id_mark: "",
      identification_mark_1: "",
      identification_mark_2: "",
      rollno: "",
      surname: "",
      fullname: "",
      fathername: "",
      mothername: "",
      fatherphoneno: "",
      motherphoneno: "",
      bloodgroup: "",
      phctype: "",
      studentstatus: "",
      certificatesin: "",
      certificates_received: "",
      admission_no: "",
      admission_date: "",
      admittedbatch_id: "",
      branch_id: "",
      second_language: "",
      seat_type: "",
      section: "",
      academic_year: currentAcademicYear,
      address: "",
    });
  };
  const handleCloseEnrollDialog = () => setEnrollDialogOpen(false);
  const handleEnrollTabChange = (e, v) => setEnrollTab(v);
  const handleEnrollInputChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...enrollForm, [name]: value };
    if (name === "rollno") {
      updated.student_rollno = value;
    }
    if (name === "admittedbatch_id") updated.branch_id = "";
    if (name === "fullname") {
      updated.student_name = value;
    }
    if (name === "fathername") {
      updated.father_name = value;
    }
    if (name === "mothername") {
      updated.mother_name = value;
    }
    if (name === "studentstatus") {
      updated.student_status = value ? value.toUpperCase() : "";
    }
    setEnrollForm(updated);
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (!clientId || !collegeId) return;

    const fetchLookups = async () => {
      const results = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/admittedbatches/${clientId}/${collegeId}`),
        axios.get(`${API_BASE_URL}/api/branches/${clientId}/${collegeId}`),
        axios.get(`${API_BASE_URL}/api/map_admittedbatch_branch/${clientId}/${collegeId}`),
        axios.get(`${API_BASE_URL}/api/acyear/by-admittedbatch/${clientId}/${collegeId}/current`),
      ]);

      const [batchResult, branchResult, mappingResult, academicYearResult] = results;

      if (batchResult.status === "fulfilled") {
        const batchRes = batchResult.value;
        const batchItems = batchRes.data?.items || batchRes.data?.data || batchRes.data || [];
        setBatches(Array.isArray(batchItems) ? batchItems : []);
      } else {
        setBatches([]);
      }

      if (branchResult.status === "fulfilled") {
        const branchRes = branchResult.value;
        const branchItems = branchRes.data?.data || branchRes.data?.items || branchRes.data || [];
        setBranches(Array.isArray(branchItems) ? branchItems : []);
      } else {
        setBranches([]);
      }

      if (mappingResult.status === "fulfilled") {
        const mappingRes = mappingResult.value;
        const mappingItems = Array.isArray(mappingRes.data)
          ? mappingRes.data
          : mappingRes.data?.data || [];
        setBatchBranchMappings(Array.isArray(mappingItems) ? mappingItems : []);
      } else {
        setBatchBranchMappings([]);
      }

      if (academicYearResult.status === "fulfilled") {
        const academicYearRes = academicYearResult.value;
        const currentYear = academicYearRes.data?.data?.academic_year || "";
        setCurrentAcademicYear(currentYear);
        setEnrollForm((previous) => ({ ...previous, academic_year: currentYear }));
      }

      const lookupNames = ["batches", "branches", "batch-branch mappings", "academic year"];
      const failedNames = results
        .map((result, index) => (result.status === "rejected" ? lookupNames[index] : null))
        .filter(Boolean);
      if (failedNames.length) {
        const firstFailure = results.find((result) => result.status === "rejected");
        console.error(
          "Error fetching enrollment lookups:",
          firstFailure.reason?.response?.data || firstFailure.reason?.message
        );
        showSnackbar(`Failed to fetch ${failedNames.join(", ")}`, "error");
      }
    };

    fetchLookups();
  }, [API_BASE_URL, clientId, collegeId]);

  const getBranchesForBatch = (admittedbatchId) => {
    if (!admittedbatchId) return [];
    const mappedBranches = batchBranchMappings
      .filter((mapping) => String(mapping.admittedbatch_id) === String(admittedbatchId))
      .map((mapping) => {
        if (mapping.branch?.branch_id) return mapping.branch;
        return branches.find((branch) => String(branch.branch_id) === String(mapping.branch_id));
      })
      .filter(Boolean);

    return Array.from(
      new Map(mappedBranches.map((branch) => [String(branch.branch_id), branch])).values()
    );
  };

  const filteredRows = useMemo(() => {
    if (!searchText) return rows;
    const key = searchText.toLowerCase();
    return rows.filter(
      (row) =>
        (row.program_name || "").toLowerCase().includes(key) ||
        (row.admitted_batch || "").toLowerCase().includes(key) ||
        (row.branch_name || "").toLowerCase().includes(key)
    );
  }, [rows, searchText]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      if (!clientId || !collegeId) {
        showSnackbar("Missing client/college context", "error");
        setRows([]);
        setLoading(false);
        return;
      }

      const res = await axios.get(
        `${API_BASE_URL}/api/students/student-summary/${clientId}/${collegeId}`
      );
      if (res.data.success && Array.isArray(res.data.data)) {
        setRows(res.data.data.map((row, idx) => ({ ...row, sno: idx + 1 })));
      } else {
        setRows([]);
        showSnackbar(res.data.message || "No data found", "warning");
      }
    } catch (err) {
      console.error("Error fetching students:", err.response?.data || err.message);
      showSnackbar(err.response?.data?.message || "Failed to fetch students", "error");
      setRows([]);
    }
    setLoading(false);
  };

  const handleView = async (row) => {
    setSelectedRow(row);
    setDialogOpen(true);
    setRelatedLoading(true);
    try {
      if (!clientId || !collegeId) {
        showSnackbar("Missing client/college context", "error");
        setRelatedStudents([]);
        setRelatedLoading(false);
        return;
      }
      const admittedbatchId = row.admittedbatch_id;
      const branchId = row.branch_id;
      const res = await axios.get(
        `${API_BASE_URL}/api/students/${clientId}/${collegeId}/${admittedbatchId}/${branchId}`
      );
      if (res.data.success && Array.isArray(res.data.data)) {
        setRelatedStudents(res.data.data);
      } else {
        setRelatedStudents([]);
      }
    } catch (err) {
      setRelatedStudents([]);
    }
    setRelatedLoading(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedRow(null);
    setRelatedStudents([]);
  };

  const handleViewStudentDetails = async (student) => {
    setStudentDetailsDialogOpen(true);
    setStudentDetails(null);
    try {
      if (!clientId || !collegeId) {
        showSnackbar("Missing client/college context", "error");
        setStudentDetails(student);
        return;
      }
      // Try fetching by student_id first (preferred). If not available, try by rollno.
      let res = null;
      if (student.student_id) {
        try {
          res = await axios.get(
            `${API_BASE_URL}/api/student-enrollment/${clientId}/${collegeId}/${student.student_id}`
          );
        } catch (e) {
          res = null;
        }
      }
      if (!res || !res.data) {
        // fallback to rollno lookup if id lookup failed or returned no data
        try {
          const roll = encodeURIComponent(student.student_rollno || student.rollno || "");
          if (roll) {
            res = await axios.get(
              `${API_BASE_URL}/api/students/rollno/${clientId}/${collegeId}/${roll}`
            );
          }
        } catch (e) {
          res = null;
        }
      }

      // Normalize server response: prefer res.data.student, then res.data.data, then res.data
      let payload = null;
      if (res && res.data) {
        if (res.data.student) payload = res.data.student;
        else if (res.data.data) payload = res.data.data;
        else payload = res.data;
      }

      // If payload is an array (some APIs return [student]), pick first
      if (Array.isArray(payload) && payload.length > 0) payload = payload[0];

      if (payload && typeof payload === "object") {
        setStudentDetails({ ...payload, ...(payload.extra_attributes || {}) });
      } else {
        setStudentDetails(student); // fallback to minimal
      }
    } catch (err) {
      setStudentDetails(student); // fallback to minimal
    }
  };

  const handleViewStudentDetailsWithTabs = async (student) => {
    setStudentDetailsDialogOpen(true);
    setStudentDetails(null);
    setEnrollTab(0); // Default to the first tab
    try {
      if (!clientId || !collegeId) {
        showSnackbar("Missing client/college context", "error");
        setStudentDetails(student);
        return;
      }

      let res = null;
      if (student.student_id) {
        try {
          res = await axios.get(
            `${API_BASE_URL}/api/student-enrollment/${clientId}/${collegeId}/${student.student_id}`
          );
        } catch (e) {
          res = null;
        }
      }
      if (!res || !res.data) {
        const roll = encodeURIComponent(student.student_rollno || student.rollno || "");
        if (roll) {
          res = await axios.get(
            `${API_BASE_URL}/api/students/rollno/${clientId}/${collegeId}/${roll}`
          );
        }
      }

      let payload = null;
      if (res && res.data) {
        if (res.data.student) payload = res.data.student;
        else if (res.data.data) payload = res.data.data;
        else payload = res.data;
      }

      if (Array.isArray(payload) && payload.length > 0) payload = payload[0];

      if (payload && typeof payload === "object") {
        setStudentDetails({ ...payload, ...(payload.extra_attributes || {}) });
      } else {
        setStudentDetails(student);
      }
    } catch (err) {
      setStudentDetails(student);
    }
  };

  const handleCloseStudentDetailsDialog = () => {
    setStudentDetailsDialogOpen(false);
    setStudentDetails(null);
  };

  const handleStudentDetailsChange = (field, value) => {
    setStudentDetails((prev) => ({
      ...(prev || {}),
      [field]: value,
      ...(field === "admittedbatch_id" ? { branch_id: "" } : {}),
    }));
  };

  const handleSaveStudentDetails = async () => {
    if (!studentDetails) return;
    try {
      if (!clientId || !collegeId) {
        showSnackbar("Missing client/college context", "error");
        return;
      }
      if (!studentDetails.student_id) {
        showSnackbar("Student id missing. Cannot update.", "error");
        return;
      }
      setStudentSaveLoading(true);
      // Prepare payload — send relevant editable fields
      const payload = { ...studentDetails };
      payload.academic_year = payload.academic_year || currentAcademicYear;
      // Some backends expect numeric ids — ensure numeric where appropriate
      if (payload.admittedbatch_id) payload.admittedbatch_id = Number(payload.admittedbatch_id);
      if (payload.branch_id) payload.branch_id = Number(payload.branch_id);
      if (payload.client_id) payload.client_id = Number(payload.client_id);
      if (payload.college_id) payload.college_id = Number(payload.college_id);

      const res = await fetch(
        `${API_BASE_URL || ""}/api/student-enrollment/${clientId}/${collegeId}/${
          studentDetails.student_id
        }`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }
      if (res.ok && data && data.success) {
        showSnackbar("Student updated successfully", "success");
        // refresh lists
        fetchStudents();
        if (selectedRow) handleView(selectedRow);
        setStudentDetailsDialogOpen(false);
        setStudentDetails(null);
      } else {
        const msg =
          (data && (data.message || JSON.stringify(data))) || `Update failed (${res.status})`;
        showSnackbar(msg, "error");
      }
    } catch (err) {
      showSnackbar("Failed to save student: " + err.message, "error");
    } finally {
      setStudentSaveLoading(false);
    }
  };

  // Add delete functionality
  const handleDeleteStudent = async (studentId) => {
    if (!studentId) {
      showSnackbar("Student ID is missing. Cannot delete.", "error");
      return;
    }

    try {
      if (!clientId || !collegeId) {
        showSnackbar("Missing client/college context.", "error");
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/api/student-enrollment/${clientId}/${collegeId}/${studentId}`,
        {
          method: "DELETE",
        }
      );

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }

      if (res.ok && data && data.success) {
        showSnackbar("Student deleted successfully.", "success");
        fetchStudents(); // Refresh the student list
      } else {
        const msg =
          (data && (data.message || JSON.stringify(data))) || `Delete failed (${res.status})`;
        showSnackbar(msg, "error");
      }
    } catch (err) {
      showSnackbar(`Failed to delete student: ${err.message}`, "error");
    }
  };

  const handleDeleteSummaryRow = (row) => {
    if (row.student_id) {
      handleDeleteStudent(row.student_id);
      return;
    }

    showSnackbar("Open View Details to delete an individual student.", "warning");
  };

  const gridColumns = useMemo(
    () => [
      {
        field: "serial",
        headerName: "SNo",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => params.api.getRowIndex(params.id) + 1,
      },
      { field: "program_name", headerName: "Program", flex: 1.2, minWidth: 180 },
      { field: "admitted_batch", headerName: "Batch", flex: 0.8, minWidth: 130 },
      { field: "branch_name", headerName: "Branch", flex: 1.2, minWidth: 180 },
      {
        field: "count",
        headerName: "EnrollStrength",
        flex: 0.8,
        minWidth: 140,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.8,
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <StudentEnrollmentActionsMenu
            row={params.row}
            onViewDetails={handleView}
            onDelete={handleDeleteSummaryRow}
          />
        ),
      },
    ],
    [handleView]
  );

  const relatedStudentColumns = useMemo(
    () => [
      {
        field: "serial",
        headerName: "SNo",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => params.api.getRowIndex(params.id) + 1,
      },
      { field: "student_name", headerName: "Student Name", flex: 1, minWidth: 180 },
      { field: "student_rollno", headerName: "Roll No", flex: 0.8, minWidth: 140 },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.8,
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <StudentEnrollmentActionsMenu
            row={params.row}
            onViewDetails={handleViewStudentDetails}
            onDelete={(row) => handleDeleteStudent(row.student_id)}
            deleteLabel="Delete Student"
          />
        ),
      },
    ],
    []
  );

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          {/* Student Enrolment Management Bar moved to top */}
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={1}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" color="white">
                  STUDENT ENROLMENT MANAGEMENT
                </MDTypography>
                <Box />
              </MDBox>
            </MDBox>
            <Paper elevation={3} sx={{ p: 2, mt: 2 }}>
              <Toolbar sx={{ px: "0 !important", gap: 2, flexWrap: "wrap" }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                  STUDENT PROFILE
                </Typography>
                <TextField
                  placeholder="Search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: { xs: "100%", sm: 320 } }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenEnrollDialog}
                  sx={{ color: "#fff" }}
                >
                  Enroll Student
                </Button>
              </Toolbar>
              <Box height={560} mt={2}>
                <DataGrid
                  rows={filteredRows}
                  columns={gridColumns}
                  loading={loading}
                  pagination
                  page={page}
                  pageSize={pageSize}
                  onPageChange={(newPage) => setPage(newPage)}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  disableSelectionOnClick
                  getRowId={(row) =>
                    row.id ||
                    `${row.program_id || row.program_name}-${
                      row.admittedbatch_id || row.admitted_batch
                    }-${row.branch_id || row.branch_name}`
                  }
                  sx={{
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f2f6fb",
                      color: "#1f3b64",
                      fontWeight: 700,
                    },
                  }}
                />
              </Box>
            </Paper>
            <Dialog
              open={enrollDialogOpen}
              onClose={handleCloseEnrollDialog}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle
                sx={{
                  bgcolor: "#1976d2",
                  color: "white",
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: 18,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>
                  ADD STUDENT PROFILE{" "}
                  <span style={{ fontWeight: 400, fontSize: 15, color: "#ffe600", marginLeft: 8 }}>
                    (Once data is entered as <span style={{ color: "#7fff00" }}>ENROLLED</span>, Do
                    not change data. After assigning RollNos, We can change data.)
                  </span>
                </span>
                <IconButton
                  aria-label="Close"
                  onClick={handleCloseEnrollDialog}
                  sx={{ color: "white" }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0, background: "#f8fafc" }}>
                <form onSubmit={handleEnrollSubmit} encType="multipart/form-data">
                  <Box sx={{ p: 3, background: "#fff", borderRadius: 2, boxShadow: 2 }}>
                    {/* Top grid: 4 columns */}
                    <Box
                      sx={{
                        mb: 2,
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" },
                        gap: 2,
                      }}
                    >
                      <TextField
                        label={
                          <span style={{ color: "red", fontWeight: 600 }}>
                            RollNo(Without Space)/Auto generation
                          </span>
                        }
                        name="rollno"
                        value={enrollForm.rollno || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                      />
                      <TextField
                        label={<span style={{ color: "red", fontWeight: 600 }}>SurName</span>}
                        name="surname"
                        value={enrollForm.surname || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                      />
                      <TextField
                        label={<span style={{ color: "red", fontWeight: 600 }}>FullName</span>}
                        name="fullname"
                        value={enrollForm.fullname || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                      />
                      <TextField
                        label={<span style={{ color: "red", fontWeight: 600 }}>FatherName</span>}
                        name="fathername"
                        value={enrollForm.fathername || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                      />
                      <TextField
                        label={<span style={{ color: "red", fontWeight: 600 }}>MotherName</span>}
                        name="mothername"
                        value={enrollForm.mothername || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                      />
                      <TextField
                        label="FatherOccupation"
                        name="fatheroccupation"
                        value={enrollForm.fatheroccupation || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                      />
                      <TextField
                        label="MotherOccupation"
                        name="motheroccupation"
                        value={enrollForm.motheroccupation || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                      />
                      <TextField
                        label="FamilyIncome"
                        name="familyincome"
                        value={enrollForm.familyincome || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                        type="number"
                      />
                      <TextField
                        label={<span style={{ color: "red", fontWeight: 600 }}>FatherPhoneNo</span>}
                        name="fatherphoneno"
                        value={enrollForm.fatherphoneno || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                      />
                      <TextField
                        label={<span style={{ color: "red", fontWeight: 600 }}>MotherPhoneNo</span>}
                        name="motherphoneno"
                        value={enrollForm.motherphoneno || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                      />
                      <TextField
                        select
                        label="Blood Group"
                        name="bloodgroup"
                        value={enrollForm.bloodgroup || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{ native: true, displayEmpty: true }}
                      >
                        <option value="">Select Blood Group</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                      </TextField>
                      <TextField
                        select
                        label="PHC Type"
                        name="phctype"
                        value={enrollForm.phctype || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{ native: true, displayEmpty: true }}
                      >
                        <option value="">Select PHC Type</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </TextField>
                      <TextField
                        select
                        label="Student Status"
                        name="studentstatus"
                        value={enrollForm.studentstatus || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{ native: true, displayEmpty: true }}
                      >
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </TextField>
                      <TextField
                        select
                        label="Certificates"
                        name="certificates_received"
                        value={enrollForm.certificates_received || ""}
                        onChange={handleEnrollInputChange}
                        size="medium"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{ native: true, displayEmpty: true }}
                      >
                        <option value="">Select Certificates</option>
                        <option value="YES">Yes</option>
                        <option value="NO">No</option>
                      </TextField>
                      {/* Photo upload removed — file uploads handled separately if needed */}
                      <MDButton
                        type="button"
                        onClick={handleEnrollSave}
                        color="success"
                        variant="contained"
                        sx={{ fontWeight: 600, height: 40, alignSelf: "center" }}
                      >
                        Save
                      </MDButton>
                    </Box>
                    {/* Tabs Section */}
                    <Tabs
                      value={enrollTab}
                      onChange={handleEnrollTabChange}
                      sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                    >
                      <Tab
                        label="Personal"
                        sx={{ bgcolor: enrollTab === 0 ? "#e8f4f8" : "transparent" }}
                      />
                      <Tab
                        label="Admission"
                        sx={{ bgcolor: enrollTab === 1 ? "#e8f4f8" : "transparent" }}
                      />
                      <Tab
                        label="Academics"
                        sx={{ bgcolor: enrollTab === 2 ? "#e8f4f8" : "transparent" }}
                      />
                      <Tab
                        label="Address"
                        sx={{ bgcolor: enrollTab === 3 ? "#e8f4f8" : "transparent" }}
                      />
                    </Tabs>
                    {/* Personal Tab: core_mstr_student + core_mstr_student_ext */}
                    {enrollTab === 0 && (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(4, 1fr)",
                          },
                          gap: 2,
                          pt: 2,
                        }}
                      >
                        <TextField
                          label={
                            <span style={{ color: "red", fontWeight: 600 }}>Aadhaar Number *</span>
                          }
                          name="aadhaar_no"
                          value={enrollForm.aadhaar_no || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          error={!!formErrors.aadhaar_no}
                          helperText={formErrors.aadhaar_no}
                        />
                        <TextField
                          label={
                            <span style={{ color: "red", fontWeight: 600 }}>Student Name *</span>
                          }
                          name="student_name"
                          value={enrollForm.student_name || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          error={!!formErrors.student_name}
                          helperText={formErrors.student_name}
                        />
                        <TextField
                          label={
                            <span style={{ color: "red", fontWeight: 600 }}>Date of Birth *</span>
                          }
                          name="dob"
                          value={enrollForm.dob || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          error={!!formErrors.dob}
                          helperText={formErrors.dob}
                        />
                        <TextField
                          select
                          label={<span style={{ color: "red", fontWeight: 600 }}>Gender *</span>}
                          name="gender"
                          value={enrollForm.gender || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          SelectProps={{ native: true }}
                          InputLabelProps={{ shrink: true }}
                          error={!!formErrors.gender}
                          helperText={formErrors.gender}
                        >
                          <option value="">Select Gender</option>
                          <option value="M">Male</option>
                          <option value="F">Female</option>
                          <option value="O">Other</option>
                        </TextField>
                        <TextField
                          label={<span style={{ color: "red", fontWeight: 600 }}>Mobile *</span>}
                          name="mobile"
                          value={enrollForm.mobile || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          error={!!formErrors.mobile}
                          helperText={formErrors.mobile}
                        />
                        <TextField
                          label="Email"
                          name="email"
                          value={enrollForm.email || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          error={!!formErrors.email}
                          helperText={formErrors.email}
                        />
                        <TextField
                          label="Identification Mark 1"
                          name="identification_mark_1"
                          value={enrollForm.identification_mark_1 || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Identification Mark 2"
                          name="identification_mark_2"
                          value={enrollForm.identification_mark_2 || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                        {/* Photo upload disabled here; keep any existing photo_url from form */}
                      </Box>
                    )}
                    {/* Academics Tab: core_mstr_student */}
                    {enrollTab === 2 && (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(3, 1fr)",
                          },
                          gap: 2,
                          pt: 2,
                        }}
                      >
                        <TextField
                          label="Second Language"
                          name="second_language"
                          value={enrollForm.second_language || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Section"
                          name="section"
                          value={enrollForm.section || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          inputProps={{ maxLength: 1 }}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Academic Year (Current)"
                          name="academic_year"
                          value={enrollForm.academic_year || currentAcademicYear}
                          size="medium"
                          fullWidth
                          disabled
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Transfer Credits"
                          name="transfer_credits"
                          value={enrollForm.transfer_credits || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          type="number"
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          select
                          label="Enroll Status"
                          name="enroll_status"
                          value={enrollForm.enroll_status || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          SelectProps={{ native: true }}
                          InputLabelProps={{ shrink: true }}
                        >
                          <option value="">Select Status</option>
                          <option value="ADMITTED">Admitted</option>
                          <option value="ENROLLED">Enrolled</option>
                          <option value="DROPPED">Dropped</option>
                        </TextField>
                      </Box>
                    )}
                    {/* Admission Tab: core_mstr_student */}
                    {enrollTab === 1 && (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: 2,
                          pt: 2,
                        }}
                      >
                        <TextField
                          label="Admission No"
                          name="admission_no"
                          value={enrollForm.admission_no || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          error={!!formErrors.admission_no}
                          helperText={formErrors.admission_no}
                        />
                        <TextField
                          select
                          label="Admitted Batch"
                          name="admittedbatch_id"
                          value={enrollForm.admittedbatch_id || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          SelectProps={{ native: true, displayEmpty: true }}
                          error={!!formErrors.admittedbatch_id}
                          helperText={formErrors.admittedbatch_id}
                        >
                          <option value="">Select Batch</option>
                          {batches.map((batch) => (
                            <option key={batch.admittedbatch_id} value={batch.admittedbatch_id}>
                              {batch.admittedbatch}
                            </option>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="Branch"
                          name="branch_id"
                          value={enrollForm.branch_id || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          disabled={!enrollForm.admittedbatch_id}
                          InputLabelProps={{ shrink: true }}
                          SelectProps={{ native: true, displayEmpty: true }}
                          error={!!formErrors.branch_id}
                          helperText={formErrors.branch_id}
                        >
                          <option value="">Select Branch</option>
                          {getBranchesForBatch(enrollForm.admittedbatch_id).map((branch) => (
                            <option key={branch.branch_id} value={branch.branch_id}>
                              {branch.branch_name}
                            </option>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="Seat Type"
                          name="seat_type"
                          value={enrollForm.seat_type || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          SelectProps={{ native: true }}
                          error={!!formErrors.seat_type}
                          helperText={formErrors.seat_type}
                        >
                          <option value="">Select Seat Type</option>
                          <option value="CONVENER">Convener</option>
                          <option value="MANAGEMENT">Management</option>
                        </TextField>
                        <TextField
                          select
                          label="Admission Type"
                          name="admission_type"
                          value={enrollForm.admission_type || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          SelectProps={{ native: true }}
                          InputLabelProps={{ shrink: true }}
                        >
                          <option value="">Select Type</option>
                          <option value="REGULAR">Regular</option>
                          <option value="LATERAL">Lateral</option>
                          <option value="TRANSFER">Transfer</option>
                          <option value="REJOIN">Rejoin</option>
                        </TextField>
                        <TextField
                          label="Previous College"
                          name="previous_college"
                          value={enrollForm.previous_college || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Transfer Date"
                          name="transfer_date"
                          value={enrollForm.transfer_date || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          type="date"
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Break Reason"
                          name="break_reason"
                          value={enrollForm.break_reason || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    )}
                    {/* Address Tab: core_mstr_student + core_mstr_student_ext */}
                    {enrollTab === 3 && (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: 2,
                          pt: 2,
                        }}
                      >
                        <TextField
                          label="Address"
                          name="address"
                          value={enrollForm.address || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          multiline
                          rows={2}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Bank Name"
                          name="bankname"
                          value={enrollForm.bankname || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Bank No"
                          name="bankno"
                          value={enrollForm.bankno || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="IFSC"
                          name="ifsc"
                          value={enrollForm.ifsc || ""}
                          onChange={handleEnrollInputChange}
                          size="medium"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    )}
                    {/* Submit button row */}
                  </Box>
                </form>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2 }}>
                <MDButton type="button" onClick={handleCloseEnrollDialog} color="secondary">
                  Cancel
                </MDButton>
                <MDButton
                  type="button"
                  onClick={handleEnrollSave}
                  color="success"
                  variant="contained"
                  sx={{ fontWeight: 600 }}
                >
                  Save Profile
                </MDButton>
              </DialogActions>
            </Dialog>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          elevation={6}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Related Students List</DialogTitle>
        <DialogContent>
          {relatedLoading ? (
            <div>Loading...</div>
          ) : relatedStudents.length === 0 ? (
            <div>No students found for this group.</div>
          ) : (
            <Box height={420} mt={1}>
              <DataGrid
                rows={relatedStudents}
                columns={relatedStudentColumns}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
                disableSelectionOnClick
                getRowId={(row) => row.student_id || row.student_rollno}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleDialogClose} color="info">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
      {/* Student Details Dialog (not nested) */}
      <Dialog
        open={studentDetailsDialogOpen}
        onClose={handleCloseStudentDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Student Details</DialogTitle>
        <DialogContent>
          <Tabs
            value={enrollTab}
            onChange={(e, v) => setEnrollTab(v)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          >
            <Tab label="Personal" />
            <Tab label="Admission" />
            <Tab label="Academics" />
            <Tab label="Address" />
          </Tabs>

          {enrollTab === 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              <TextField
                label="Student Name *"
                value={studentDetails?.student_name || ""}
                onChange={(e) => handleStudentDetailsChange("student_name", e.target.value)}
                fullWidth
                error={!studentDetails?.student_name}
                helperText={!studentDetails?.student_name ? "Required" : ""}
              />
              <TextField
                label="Roll No *"
                value={studentDetails?.student_rollno || ""}
                onChange={(e) => handleStudentDetailsChange("student_rollno", e.target.value)}
                fullWidth
                error={!studentDetails?.student_rollno}
                helperText={!studentDetails?.student_rollno ? "Required" : ""}
              />
              <TextField
                label="Gender *"
                value={studentDetails?.gender || ""}
                onChange={(e) => handleStudentDetailsChange("gender", e.target.value)}
                fullWidth
                error={!studentDetails?.gender}
                helperText={!studentDetails?.gender ? "Required" : ""}
              />
              <TextField
                label="Mobile *"
                value={studentDetails?.mobile || ""}
                onChange={(e) => handleStudentDetailsChange("mobile", e.target.value)}
                fullWidth
                error={!studentDetails?.mobile}
                helperText={!studentDetails?.mobile ? "Required" : ""}
              />
              <TextField
                label="Date of Birth *"
                value={formatDateForInput(studentDetails?.dob)}
                onChange={(e) => handleStudentDetailsChange("dob", e.target.value)}
                fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                error={!studentDetails?.dob}
                helperText={!studentDetails?.dob ? "Required" : ""}
              />
              <TextField
                label="Identification Mark 1"
                value={studentDetails?.identification_mark_1 || ""}
                onChange={(e) =>
                  handleStudentDetailsChange("identification_mark_1", e.target.value)
                }
                fullWidth
              />
              <TextField
                label="Identification Mark 2"
                value={studentDetails?.identification_mark_2 || ""}
                onChange={(e) =>
                  handleStudentDetailsChange("identification_mark_2", e.target.value)
                }
                fullWidth
              />
            </Box>
          )}

          {enrollTab === 1 && (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
              <TextField
                label="Admission No *"
                value={studentDetails?.admission_no || ""}
                onChange={(e) => handleStudentDetailsChange("admission_no", e.target.value)}
                fullWidth
                error={!studentDetails?.admission_no}
                helperText={!studentDetails?.admission_no ? "Required" : ""}
              />
              <TextField
                select
                label="Admitted Batch *"
                value={studentDetails?.admittedbatch_id || ""}
                onChange={(e) => handleStudentDetailsChange("admittedbatch_id", e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                error={!studentDetails?.admittedbatch_id}
                helperText={!studentDetails?.admittedbatch_id ? "Required" : ""}
              >
                <option value="">Select Batch</option>
                {batches.map((batch) => (
                  <option key={batch.admittedbatch_id} value={batch.admittedbatch_id}>
                    {batch.admittedbatch}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                label="Branch *"
                value={studentDetails?.branch_id || ""}
                onChange={(e) => handleStudentDetailsChange("branch_id", e.target.value)}
                fullWidth
                disabled={!studentDetails?.admittedbatch_id}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                error={!studentDetails?.branch_id}
                helperText={!studentDetails?.branch_id ? "Required" : ""}
              >
                <option value="">Select Branch</option>
                {getBranchesForBatch(studentDetails?.admittedbatch_id).map((branch) => (
                  <option key={branch.branch_id} value={branch.branch_id}>
                    {branch.branch_name}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                label="Seat Type *"
                value={studentDetails?.seat_type || ""}
                onChange={(e) => handleStudentDetailsChange("seat_type", e.target.value)}
                fullWidth
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                error={!studentDetails?.seat_type}
                helperText={!studentDetails?.seat_type ? "Required" : ""}
              >
                <option value="">Select Seat Type</option>
                <option value="CONVENER">Convener</option>
                <option value="MANAGEMENT">Management</option>
              </TextField>
            </Box>
          )}

          {enrollTab === 2 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              <TextField
                label="Second Language *"
                value={studentDetails?.second_language || ""}
                onChange={(e) => handleStudentDetailsChange("second_language", e.target.value)}
                fullWidth
                error={!studentDetails?.second_language}
                helperText={!studentDetails?.second_language ? "Required" : ""}
              />
              <TextField
                select
                label="Certificates"
                value={studentDetails?.certificates_received || ""}
                onChange={(e) =>
                  handleStudentDetailsChange("certificates_received", e.target.value)
                }
                fullWidth
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Select</option>
                <option value="YES">Yes</option>
                <option value="NO">No</option>
              </TextField>
              <TextField
                label="Section"
                value={studentDetails?.section || ""}
                onChange={(e) => handleStudentDetailsChange("section", e.target.value)}
                fullWidth
                inputProps={{ maxLength: 1 }}
              />
              <TextField
                label="Academic Year (Current)"
                value={studentDetails?.academic_year || currentAcademicYear}
                fullWidth
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}

          {enrollTab === 3 && (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
              <TextField
                label="Address *"
                value={studentDetails?.address || ""}
                onChange={(e) => handleStudentDetailsChange("address", e.target.value)}
                fullWidth
                error={!studentDetails?.address}
                helperText={!studentDetails?.address ? "Required" : ""}
              />
              <TextField
                label="Bank Name *"
                value={studentDetails?.bankname || ""}
                onChange={(e) => handleStudentDetailsChange("bankname", e.target.value)}
                fullWidth
                error={!studentDetails?.bankname}
                helperText={!studentDetails?.bankname ? "Required" : ""}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseStudentDetailsDialog} color="info">
            Close
          </MDButton>
          <MDButton
            onClick={handleSaveStudentDetails}
            color="success"
            disabled={studentSaveLoading}
          >
            {studentSaveLoading ? "Saving..." : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
export default StudentEnrollement;
